import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row, Alert } from "reactstrap";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { partyState } from "../../Recoil/atom";
import { getLevel } from "../../constants/items";
import StatsModal from "../../components/Common/StatsModal";
import api from "../../api";

const PartyWidget = () => {
  const partyValue = useRecoilValue(partyState);
  const setPartyState = useSetRecoilState(partyState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // stats modal state
  const [statsModal, setStatsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const toggleStatsModal = () => setStatsModal(on => !on);

  // Fetch party data on component mount
  useEffect(() => {
    const fetchPartyData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Fetch Pokemon with slots 1-6 (party slots)
        const { data } = await api.get("/v1/pokemon?page=1&limit=20", { withCredentials: true });
        
        // Filter for party Pokemon (slots 1-6) and sort by slot
        const partyPokemon = data.data
          .filter(pokemon => pokemon.slot >= 1 && pokemon.slot <= 6)
          .sort((a, b) => a.slot - b.slot);
        
        setPartyState(partyPokemon);
      } catch (err) {
        console.error("Failed to fetch party data:", err);
        setError("Failed to load party data");
      } finally {
        setLoading(false);
      }
    };

    fetchPartyData();
  }, [setPartyState]);

  const openStats = async (mon) => {
    setModalLoading(true);
    setStatsModal(true);
    try {
      const { data } = await api.get(`/v1/pokemon/${mon.id}`, { withCredentials: true });
      setModalData(data);
    } catch (err) {
      setModalData({ error: err.response?.data?.detail || "Failed to load" });
    } finally {
      setModalLoading(false);
    }
  };

  if (loading) {
    return (
      <React.Fragment>
        <h5 style={{ 
          color: "#ffffff", 
          fontWeight: "600",
          marginBottom: "1rem",
          paddingLeft: "0.5rem",
          borderLeft: "3px solid rgba(16, 185, 129, 0.8)"
        }}>Your Party</h5>
        <div style={{ 
          color: "rgba(255, 255, 255, 0.7)",
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          borderRadius: "8px",
          padding: "1.5rem",
          textAlign: "center",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          margin: "0 15px"
        }}>
          <div className="spinner-border text-primary" role="status" style={{ width: "2rem", height: "2rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 mb-0">Loading your party...</p>
        </div>
      </React.Fragment>
    );
  }

  if (error) {
    return (
      <React.Fragment>
        <h5 style={{ 
          color: "#ffffff", 
          fontWeight: "600",
          marginBottom: "1rem",
          paddingLeft: "0.5rem",
          borderLeft: "3px solid rgba(16, 185, 129, 0.8)"
        }}>Your Party</h5>
        <Alert color="warning" className="mx-3">
          <h6>Party Unavailable</h6>
          <small>{error}</small>
        </Alert>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <h5 style={{ 
        color: "#ffffff", 
        fontWeight: "600",
        marginBottom: "1rem",
        paddingLeft: "0.5rem",
        borderLeft: "3px solid rgba(16, 185, 129, 0.8)"
      }}>Your Party</h5>
      <Row>
        {partyValue.length > 0 ? (
          partyValue.map((member, index) => (
            <Col key={member.id || index} md="3" sm="6" className="mb-3">
              <Card
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  border: "none",
                  overflow: "hidden",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  cursor: "pointer",
                  backgroundColor: "#1d222e"
                }}
                onClick={() => openStats(member)}
                className="hover-effect"
              >
                <CardBody className="text-center" style={{
                  backgroundColor: "#242836",
                  padding: "1.25rem"
                }}>
                  <img
                    src={`/sprites/${member.name}.png`}
                    alt={member.name}
                    style={{ width: "120px" }}
                  />
                  <h6 style={{ 
                    marginTop: "0.75rem",
                    color: "#ffffff",
                    fontWeight: "600"
                  }}>
                    {member.nickname || member.name}
                  </h6>
                  <div style={{ 
                    fontSize: "0.9rem",
                    color: "rgba(255, 255, 255, 0.8)",
                    marginTop: "0.5rem"
                  }}>
                    <div style={{
                      backgroundColor: "rgba(16, 185, 129, 0.2)",
                      color: "#10b981",
                      borderRadius: "4px",
                      padding: "0.25rem 0.5rem",
                      display: "inline-block",
                      fontWeight: "500"
                    }}>
                      Level: {getLevel(member.totalexp).toLocaleString()}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))
        ) : (
          <div style={{ 
            color: "rgba(255, 255, 255, 0.7)",
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderRadius: "8px",
            padding: "1.5rem",
            textAlign: "center",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            width: "100%",
            margin: "0 15px"
          }}>
            <i className="ri-ghost-line" style={{ fontSize: "2rem", marginBottom: "0.5rem" }}></i>
            <p>No Pokémon in your party.</p>
          </div>
        )}
      </Row>

      <StatsModal
        isOpen={statsModal}
        toggle={toggleStatsModal}
        loading={modalLoading}
        data={modalData}
      />
    </React.Fragment>
  );
};

export default PartyWidget;
